import React, { useState, useRef } from "react";
import { VendorTaglineControl } from "../components/VendorTaglineField";
import { Form, Button, Row, Col, Card, Spinner } from "react-bootstrap";
import { Switch, FormControlLabel } from '@material-ui/core';
import { MdAddAPhoto, MdDelete } from "react-icons/md";
import {
  uploadExactVendorProfileMedia,
} from "../utilities/imageUpload";
import { toast } from "react-toastify";
import VendorProfileMediaDeleteDialog from "../components/VendorProfileMediaDeleteDialog";
import { useVendorMediaCrop } from "../components/VendorMediaCrop";
import healthcarePlaceholderImage from "../assets/img/restaurantimage_unavailable.png";
import ArtworkTemplateLink from "../components/ArtworkTemplateLink";
import UploadProgress from "../components/UploadProgress";

const EditHealthcareForm = ({ healthcare, onSave, onCancel }) => {
  const fileInputRef = useRef(null);
  const { fitVendorMedia, mediaCropDialog } = useVendorMediaCrop();
  const [formData, setFormData] = useState({
    name: healthcare.name || "",
    address: healthcare.address || "",
    phone: healthcare.phone || "",
    email: healthcare.email || "",
    medicalLicenseNumber: healthcare.medicalLicenseNumber || "",
    certifications: healthcare.certifications?.join(", ") || "",
    specializations: healthcare.specializations?.join(", ") || "",
    typeOfGoods: healthcare.typeOfGoods || "",
    typeOfServices: healthcare.typeOfServices || "",
    // Left undefined when the row has none, so a save that never touched the
    // field does not send it (the server then leaves the tagline as it is).
    tagline: healthcare.tagline,
    imageUrl: healthcare.imageUrl || "",
    bannerUrl: healthcare.bannerUrl || "",
    shopBannerUrl: healthcare.shopBannerUrl || "",
    isActive: healthcare.isActive,
    latitude: healthcare.latitude || "",
    longitude: healthcare.longitude || "",
    deliveryZoneRadius: healthcare.deliveryZoneRadius ?? 10,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreview, setImagePreview] = useState(healthcare.imageUrl || "");
  const [logoFile, setLogoFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [bannerPreview, setBannerPreview] = useState(healthcare.bannerUrl || "");
  const [cardImageFile, setCardImageFile] = useState(null);
  // The saved image whose Delete link was clicked; opens the delete dialog.
  const [mediaDeleteRole, setMediaDeleteRole] = useState(null);
  const bannerFileInputRef = useRef(null);
  const [shopBannerUploading, setShopBannerUploading] = useState(false);
  const [shopBannerUploadProgress, setShopBannerUploadProgress] = useState(0);
  const [shopBannerPreview, setShopBannerPreview] = useState(healthcare.shopBannerUrl || "");
  const [shopBannerFile, setShopBannerFile] = useState(null);
  const shopBannerFileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const previewFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read this image."));
    reader.readAsDataURL(file);
  });

  const handleImageSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
      file = await fitVendorMedia(file, "healthcare", "logo");
      if (!file) return; // crop cancelled
      setLogoFile(file);
      setImagePreview(await previewFile(file));
      toast.success("Logo ready (1024x1024px)");
    } catch (error) {
      toast.error(error.message || "Failed to validate logo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleBannerSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
      file = await fitVendorMedia(file, "healthcare", "cardImage");
      if (!file) return; // crop cancelled
      setCardImageFile(file);
      setBannerPreview(await previewFile(file));
      toast.success("Landing-page card image ready (1200x800px)");
    } catch (error) {
      toast.error(error.message || "Failed to validate card image.");
    } finally {
      setBannerUploading(false);
      e.target.value = "";
    }
  };

  const handleShopBannerSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    setShopBannerUploading(true);
    try {
      // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
      file = await fitVendorMedia(file, "healthcare", "shopBanner");
      if (!file) return; // crop cancelled
      setShopBannerFile(file);
      setShopBannerPreview(await previewFile(file));
      toast.success("Web shop header banner ready (1440x175px)");
    } catch (error) {
      toast.error(error.message || "Failed to validate shop banner.");
    } finally {
      setShopBannerUploading(false);
      e.target.value = "";
    }
  };

  // An unsaved pick is only a local preview: discard it with no server call
  // and no confirmation. A saved image opens VendorProfileMediaDeleteDialog,
  // which shows every saved image and lets the operator tick more than one.
  const handleRemoveMedia = (mediaRole) => {
    const staged = {
      logo: logoFile,
      cardImage: cardImageFile,
      shopBanner: shopBannerFile,
    }[mediaRole];
    if (staged) {
      if (mediaRole === "logo") { setLogoFile(null); setImagePreview(healthcare.imageUrl || ""); }
      if (mediaRole === "cardImage") { setCardImageFile(null); setBannerPreview(healthcare.bannerUrl || ""); }
      if (mediaRole === "shopBanner") { setShopBannerFile(null); setShopBannerPreview(healthcare.shopBannerUrl || ""); }
      return;
    }
    setMediaDeleteRole(mediaRole);
  };

  // Healthcare keeps a preview per slot beside formData; clear both together.
  const handleMediaRemoved = (cleared, roles) => {
    setFormData((prev) => ({ ...prev, ...cleared }));
    if (roles.includes("logo")) setImagePreview("");
    if (roles.includes("cardImage")) setBannerPreview("");
    if (roles.includes("shopBanner")) setShopBannerPreview("");
  };

  const handleSubmitEditHealthcareOutlet = async (e) => {
    e.preventDefault();

    // Convert comma-separated strings back to arrays
    const updatedData = {
      ...formData,
      certifications: formData.certifications
        ? formData.certifications.split(",").map((cert) => cert.trim())
        : [],
      specializations: formData.specializations
        ? formData.specializations.split(",").map((spec) => spec.trim())
        : [],
    };

    try {
      const uploadOptions = {
        vendorType: "healthcare",
        vendorId: healthcare._id || healthcare.owner,
        entityType: "healthcareProfile",
        entityId: healthcare._id || "profile",
        source: "dashboard",
      };
      if (logoFile) {
        setUploading(true);
        const uploaded = await uploadExactVendorProfileMedia(logoFile, {
          ...uploadOptions,
          mediaRole: "logo",
          onProgress: setUploadProgress,
        });
        updatedData.imageUrl = uploaded.secure_url;
      }
      if (cardImageFile) {
        setBannerUploading(true);
        const uploaded = await uploadExactVendorProfileMedia(cardImageFile, {
          ...uploadOptions,
          mediaRole: "cardImage",
          onProgress: setBannerUploadProgress,
        });
        updatedData.bannerUrl = uploaded.secure_url;
      }
      if (shopBannerFile) {
        setShopBannerUploading(true);
        const uploaded = await uploadExactVendorProfileMedia(shopBannerFile, {
          ...uploadOptions,
          mediaRole: "shopBanner",
          onProgress: setShopBannerUploadProgress,
        });
        updatedData.shopBannerUrl = uploaded.secure_url;
      }
      await onSave(updatedData);
    } catch (error) {
      toast.error(error.message || "Could not save healthcare media.");
    } finally {
      setUploading(false);
      setBannerUploading(false);
      setShopBannerUploading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmitEditHealthcareOutlet}>
      <Row>
        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Healthcare Provider Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>

        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <VendorTaglineControl
        value={formData.tagline}
        onChange={handleChange}
        groupClassName="mb-3"
      />

      <Row>
        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>

        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Medical License Number</Form.Label>
            <Form.Control
              type="text"
              name="medicalLicenseNumber"
              value={formData.medicalLicenseNumber}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: "600", color: "#E91E63" }}>
              Web Shop Header Banner
            </Form.Label>
            <Card style={{ border: "2px dashed #E91E63", borderRadius: "12px" }}>
              <Card.Body style={{ padding: "20px", textAlign: "center" }}>
                {shopBannerPreview ? (
                  <div>
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "1440 / 175",
                        margin: "0 auto 15px",
                        overflow: "hidden",
                        border: "1.5px solid rgba(233,30,99,0.25)",
                        background: "#fff",
                      }}
                    >
                      <img
                        src={shopBannerPreview}
                        alt="Web shop header banner preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                      />
                    </div>
                    <small className="text-muted d-block mb-2">
                      Exact preview of the 1440x175 web shop header frame
                    </small>
                    <div className="d-flex gap-2 justify-content-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => shopBannerFileInputRef.current?.click()}
                        disabled={shopBannerUploading}
                      >
                        <MdAddAPhoto style={{ marginRight: "5px" }} />
                        Replace Shop Banner
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveMedia("shopBanner")}
                        disabled={shopBannerUploading}
                      >
                        <MdDelete style={{ marginRight: "5px" }} />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Button
                      variant="primary"
                      onClick={() => shopBannerFileInputRef.current?.click()}
                      disabled={shopBannerUploading}
                      style={{ backgroundColor: "#E91E63", borderColor: "#E91E63" }}
                    >
                      <MdAddAPhoto style={{ marginRight: "5px" }} />
                      Upload Shop Banner
                    </Button>
                    <small className="text-muted d-block mt-2">
                      Exact: 1440x175px, JPG/PNG/WebP, max 5 MB. Used only at the top of the web shop.
                    </small>
                    {/* The artwork template for this slot, when one is configured. */}
                    <ArtworkTemplateLink slotKey="healthcare.shopBanner" variant="inline" />
                  </div>
                )}
                <input
                  ref={shopBannerFileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleShopBannerSelect}
                  style={{ display: "none" }}
                />
                {shopBannerUploading && (
                  <UploadProgress percent={shopBannerUploadProgress} label="Uploading shop banner" />
                )}
              </Card.Body>
            </Card>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>Address</Form.Label>
        <Form.Control
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
        />
      </Form.Group>

      <Row>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Latitude</Form.Label>
            <Form.Control
              type="number"
              name="latitude"
              value={formData.latitude}
              onChange={handleChange}
              placeholder="e.g. 0.3476"
              step="any"
            />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Longitude</Form.Label>
            <Form.Control
              type="number"
              name="longitude"
              value={formData.longitude}
              onChange={handleChange}
              placeholder="e.g. 32.5825"
              step="any"
            />
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group className="mb-3">
            <Form.Label>Delivery Zone Radius (km)</Form.Label>
            <Form.Control
              type="number"
              name="deliveryZoneRadius"
              value={formData.deliveryZoneRadius}
              onChange={handleChange}
              min="1"
              placeholder="e.g. 10"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Certifications (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              name="certifications"
              value={formData.certifications}
              onChange={handleChange}
              placeholder="e.g., Board Certified, ISO 9001"
            />
            <Form.Text className="text-muted">
              Separate multiple certifications with commas
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Specializations (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              name="specializations"
              value={formData.specializations}
              onChange={handleChange}
              placeholder="e.g., Cardiology, Pediatrics"
            />
            <Form.Text className="text-muted">
              Separate multiple specializations with commas
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Type of Goods (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="typeOfGoods"
              value={formData.typeOfGoods}
              onChange={handleChange}
              placeholder="e.g., Medical Supplies, Pharmaceuticals"
            />
          </Form.Group>
        </Col>

        <Col md="6">
          <Form.Group className="mb-3">
            <Form.Label>Type of Services (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="typeOfServices"
              value={formData.typeOfServices}
              onChange={handleChange}
              placeholder="e.g., Consultations, Diagnostics"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: "600", color: "#E91E63" }}>
              Healthcare Provider Logo
            </Form.Label>
            <Card style={{ border: "2px dashed #E91E63", borderRadius: "12px" }}>
              <Card.Body style={{ padding: "20px", textAlign: "center" }}>
                {imagePreview ? (
                  <div>
                    <img
                      src={imagePreview}
                      alt="Logo preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        marginBottom: "15px",
                      }}
                    />
                    <div className="d-flex gap-2 justify-content-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <MdAddAPhoto style={{ marginRight: "5px" }} />
                        Replace Logo
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveMedia("logo")}
                        disabled={uploading}
                      >
                        <MdDelete style={{ marginRight: "5px" }} />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <img
                      src={healthcarePlaceholderImage}
                      alt="Placeholder"
                      style={{
                        maxWidth: "150px",
                        opacity: 0.5,
                        marginBottom: "15px",
                      }}
                    />
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        style={{ backgroundColor: "#E91E63", borderColor: "#E91E63" }}
                      >
                        <MdAddAPhoto style={{ marginRight: "5px" }} />
                        Upload Logo
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Exact: 1024x1024px, JPG/PNG/WebP, max 2 MB. Stored without resizing.
                    </small>
                    {/* The artwork template for this slot, when one is configured. */}
                    <ArtworkTemplateLink slotKey="healthcare.logo" variant="inline" />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                />
                {uploading && (
                  <UploadProgress percent={uploadProgress} label="Uploading logo" />
                )}
              </Card.Body>
            </Card>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: "600", color: "#E91E63" }}>
              Landing-Page Card Image
            </Form.Label>
            <Card style={{ border: "2px dashed #E91E63", borderRadius: "12px" }}>
              <Card.Body style={{ padding: "20px", textAlign: "center" }}>
                {bannerPreview ? (
                  <div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "360px",
                        aspectRatio: "3 / 2",
                        margin: "0 auto 15px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "0 4px 14px rgba(233,30,99,0.18)",
                        border: "1.5px solid rgba(233,30,99,0.25)",
                        background: "#fff",
                      }}
                    >
                      <img
                        src={bannerPreview}
                        alt="Banner preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: "#666",
                        marginBottom: "10px",
                      }}
                    >
                      Preview shown at the same 3:2 crop used on the landing page
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => bannerFileInputRef.current?.click()}
                        disabled={bannerUploading}
                      >
                        <MdAddAPhoto style={{ marginRight: "5px" }} />
                        Replace Banner
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveMedia("cardImage")}
                        disabled={bannerUploading}
                      >
                        <MdDelete style={{ marginRight: "5px" }} />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: "15px", color: "#999" }}>
                      <span style={{ fontSize: "2rem" }}>🖼️</span>
                    </div>
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => bannerFileInputRef.current?.click()}
                        disabled={bannerUploading}
                        style={{ backgroundColor: "#E91E63", borderColor: "#E91E63" }}
                      >
                        <MdAddAPhoto style={{ marginRight: "5px" }} />
                        Upload Banner
                      </Button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Exact: 1200x800px (3:2), JPG/PNG/WebP, max 5 MB. Stored without resizing.
                    </small>
                    {/* The artwork template for this slot, when one is configured. */}
                    <ArtworkTemplateLink slotKey="healthcare.cardImage" variant="inline" />
                  </div>
                )}
                <input
                  ref={bannerFileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleBannerSelect}
                  style={{ display: "none" }}
                />
                {bannerUploading && (
                  <UploadProgress percent={bannerUploadProgress} label="Uploading banner" />
                )}
              </Card.Body>
            </Card>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              name="isActive"
              color="secondary"
            />
          }
          label={formData.isActive ? 'Active Healthcare Vendor' : 'Deactivated Healthcare Vendor'}
        />
      </Form.Group>

      <div className="d-flex gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Save Changes
        </Button>
      </div>

      {mediaCropDialog}
      <VendorProfileMediaDeleteDialog
        role={mediaDeleteRole}
        vendorType="healthcare"
        vendorId={healthcare._id || healthcare.owner}
        vendorName={healthcare.name}
        media={formData}
        owner="the vendor"
        onClose={() => setMediaDeleteRole(null)}
        onRemoved={handleMediaRemoved}
      />
    </Form>
  );
};

export default EditHealthcareForm;

